import * as React from 'react';

import {
  enumFacilitySortOption,
  enumRoleSortOption,
} from '@rematter/frontend-lib-api-client';
import { type PaginationProps, type TinyTableSorting } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { useFacilitiesQuery } from '../_graphql/useFacilitiesQuery';
import { useRolesQuery } from '../_graphql/useRolesQuery';
import { useUserRolesMapQuery } from '../_graphql/useUserRolesMapQuery';
import { useUsersQuery } from '../_graphql/useUsersQuery';

import * as state from '~/modules/settings/_state/teamPermissions.state';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export const useUsersTableFilters = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const [facilityFilter, setFacilityFilter] = useAtom(state.$facilityFilter);
  const [rolesFilter, setRolesFilter] = useAtom(state.$rolesFilter);

  const [offset, setOffset] = useAtom(state.$usersOffset);
  const [limit, setLimit] = useAtom(state.$usersLimit);
  const [sorting, setSorting] = React.useState<TinyTableSorting>({
    column: 'name',
    direction: 'asc',
  });

  const {
    data: { users: rawUsers = [] } = { users: [] },
    isLoading,
    error,
  } = useUsersQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit: 1000,
    offset: 0,
  });

  const { data: { rolesMap = {}, roleIdsMap = {} } = {} } =
    useUserRolesMapQuery({});

  const allUsers = React.useMemo(
    () =>
      rawUsers.map((user) => ({
        ...user,
        roles: rolesMap[user.id] ?? [],
        roleIds: roleIdsMap[user.id] ?? [],
      })),
    [rawUsers, rolesMap, roleIdsMap],
  );

  const { data: { facilities = [] } = {} } = useFacilitiesQuery({
    sorting: [enumFacilitySortOption.NameAsc],
    limit: 1000,
    offset: 0,
  });

  const { data: { roles = [] } = {} } = useRolesQuery({
    sorting: [enumRoleSortOption.NameAsc],
    limit: 1000,
    offset: 0,
  });

  const filteredUsers = React.useMemo(() => {
    let result = allUsers;

    if (facilityFilter.length > 0) {
      const selectedIds = new Set(facilityFilter);
      result = result.filter((user) =>
        user.facilityIds.some((id) => selectedIds.has(id)),
      );
    }

    if (rolesFilter.length > 0) {
      const selectedIds = new Set(rolesFilter);
      result = result.filter((user) =>
        user.roleIds.some((id) => selectedIds.has(id)),
      );
    }

    return result;
  }, [allUsers, facilityFilter, rolesFilter]);

  const sortedUsers = React.useMemo(
    () =>
      [...filteredUsers].sort((a, b) => {
        const dir = sorting.direction === 'asc' ? 1 : -1;
        switch (sorting.column) {
          case 'name':
            return dir * a.name.localeCompare(b.name);
          case 'contactInfo':
            return dir * a.email.localeCompare(b.email);
          case 'facilities':
            return (
              dir * (a.facilities[0] ?? '').localeCompare(b.facilities[0] ?? '')
            );
          case 'status':
            return dir * a.status.localeCompare(b.status);
          default:
            return 0;
        }
      }),
    [filteredUsers, sorting],
  );

  const paginatedUsers = React.useMemo(
    () => sortedUsers.slice(offset, offset + limit),
    [sortedUsers, offset, limit],
  );

  const total = sortedUsers.length;

  const selectedFacilityKeys = React.useMemo(
    () => new Set<React.Key>(facilityFilter),
    [facilityFilter],
  );

  const selectedFacilities = React.useMemo(
    () => facilities.filter((f) => facilityFilter.includes(f.id)),
    [facilities, facilityFilter],
  );

  const selectedRoleKeys = React.useMemo(
    () => new Set<React.Key>(rolesFilter),
    [rolesFilter],
  );

  const selectedRoles = React.useMemo(
    () => roles.filter((r) => rolesFilter.includes(r.id)),
    [roles, rolesFilter],
  );

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleSortChange = (newSorting: TinyTableSorting) => {
    setSorting(newSorting);
    setOffset(0);
  };

  const handleFacilityFilterChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setFacilityFilter(Array.from(keys) as string[]);
      setOffset(0);
    },
    [setFacilityFilter, setOffset],
  );

  const handleFacilityFilterReset = React.useCallback(() => {
    setFacilityFilter([]);
    setOffset(0);
  }, [setFacilityFilter, setOffset]);

  const handleRolesFilterChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setRolesFilter(Array.from(keys) as string[]);
      setOffset(0);
    },
    [setRolesFilter, setOffset],
  );

  const handleRolesFilterReset = React.useCallback(() => {
    setRolesFilter([]);
    setOffset(0);
  }, [setRolesFilter, setOffset]);

  return {
    searchTerm,
    handleSearchChange,
    facilityFilter,
    facilities,
    selectedFacilities,
    selectedFacilityKeys,
    handleFacilityFilterChange,
    handleFacilityFilterReset,
    rolesFilter,
    roles,
    selectedRoles,
    selectedRoleKeys,
    handleRolesFilterChange,
    handleRolesFilterReset,
    allUsers,
    paginatedUsers,
    total,
    isLoading,
    error,
    offset,
    limit,
    sorting,
    handlePaginationChange,
    handleSortChange,
  };
};
